// League detail — F6 Slice 2. Reads :leagueId from the route and loads the
// league shell PLUS its seasons (Promise.allSettled, so the header still renders
// when the seasons fetch fails). Surfaces:
//   - Header: name <h1>, format label, status Badge, Edit dialog (name +
//     description + format), Archive/Unarchive status toggle.
//   - Seasons section: one row per season (name + UTC date range + status Badge),
//     newest-first by start_date, honest empty state + "New season" CTA, and an
//     independent error banner when the seasons fetch rejects.
//   - Create season dialog: name (required, trim-gated) + start_date + end_date
//     (both required; end_date >= start_date, blocked client-side).
//
// Renders ONLY real backend fields. league_id / season_id (UUIDs) are NEVER shown
// as visible text. Format/status are mapped to human labels (never the raw enum).
// Season rows ARE links to /leagues/seasons/{season_id} (Slice 3 — the
// season-detail page landed). The season_id drives the href but is never shown
// as visible text.

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { notifications } from "@/app/notifications";
import {
  LEAGUE_FORMATS,
  createSeason,
  getLeague,
  getSeasons,
  updateLeague,
  type LeagueFormat,
  type LeagueResponse,
  type LeagueSeasonResponse,
} from "@/libs/leaguesApi";
import {
  LEAGUE_FORMAT_LABELS,
  SEASON_STATUS_LABELS,
  STATUS_LABELS,
} from "./league-display";

// UTC date formatter — "Mar 1, 2026". The backend stores season start_date /
// end_date as DATE-typed fields it serializes as UTC midnight
// ("2026-03-01T00:00:00Z"); formatting in the browser's local zone would drift
// the day back in any west-of-UTC env, so we pin timeZone: "UTC". This is a
// LOCAL copy of the programs-list formatter (cross-module consolidation is a
// tracked follow-up — not done in this slice).
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatDateRange(
  start: string,
  end: string | null | undefined,
): string {
  const startLabel = formatDate(start);
  if (!end) return startLabel;
  return `${startLabel} – ${formatDate(end)}`;
}

export function LeagueDetail() {
  const { leagueId = "" } = useParams<{ leagueId: string }>();

  const [league, setLeague] = useState<LeagueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Seasons load independently of the league (Promise.allSettled) — the header
  // must render even if seasons fail. `seasonsError` is the seasons-specific
  // banner; it is distinct from the empty state so a failed fetch never
  // masquerades as "no seasons".
  const [seasons, setSeasons] = useState<LeagueSeasonResponse[]>([]);
  const [seasonsError, setSeasonsError] = useState<string | null>(null);

  // Edit dialog state.
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editFormat, setEditFormat] =
    useState<LeagueFormat>("rotating_doubles");
  const [savingEdit, setSavingEdit] = useState(false);
  const editInFlight = useRef(false);

  // Archive/unarchive toggle state.
  const [togglingStatus, setTogglingStatus] = useState(false);
  const statusInFlight = useRef(false);

  // Create season dialog state.
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [seasonName, setSeasonName] = useState("");
  const [seasonStart, setSeasonStart] = useState("");
  const [seasonEnd, setSeasonEnd] = useState("");
  const [savingSeason, setSavingSeason] = useState(false);
  const seasonInFlight = useRef(false);

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSeasonsError(null);
    (async () => {
      const [leagueRes, seasonsRes] = await Promise.allSettled([
        getLeague(leagueId),
        getSeasons(leagueId),
      ]);
      if (cancelled) return;
      if (leagueRes.status === "fulfilled") {
        setLeague(leagueRes.value);
      } else {
        setError(
          leagueRes.reason instanceof Error
            ? leagueRes.reason.message
            : "Failed to load this league.",
        );
      }
      if (seasonsRes.status === "fulfilled") {
        setSeasons(seasonsRes.value);
      } else {
        setSeasonsError(
          "We couldn't load the seasons for this league. Please try again.",
        );
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  // Seasons ordered newest-first by start_date (defensive sort — never trust the
  // server order). A row with an unparseable start sorts last.
  const sortedSeasons = useMemo(
    () =>
      [...seasons].sort((a, b) => {
        const at = new Date(a.start_date).getTime();
        const bt = new Date(b.start_date).getTime();
        const av = Number.isNaN(at) ? -Infinity : at;
        const bv = Number.isNaN(bt) ? -Infinity : bt;
        return bv - av;
      }),
    [seasons],
  );

  const openEdit = () => {
    if (!league) return;
    setEditName(league.name);
    setEditDescription(league.description ?? "");
    setEditFormat((league.format as LeagueFormat) ?? "rotating_doubles");
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!league) return;
    const trimmedName = editName.trim();
    // Required-name gate — whitespace-only never reaches the API.
    if (!trimmedName) return;
    if (editInFlight.current) return;
    editInFlight.current = true;
    setSavingEdit(true);
    try {
      const updated = await updateLeague(leagueId, {
        name: trimmedName,
        description: editDescription.trim() || undefined,
        format: editFormat,
      });
      setLeague(updated);
      notifications.success("League updated.");
      setEditOpen(false);
    } catch (err) {
      // Failure (incl. RBAC 403): honest notifications, header unchanged, dialog stays
      // OPEN so the operator can retry — no phantom edit.
      notifications.error(
        err instanceof Error ? err.message : "Could not update the league.",
      );
    } finally {
      editInFlight.current = false;
      setSavingEdit(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!league) return;
    if (statusInFlight.current) return;
    const next = league.status === "archived" ? "active" : "archived";
    statusInFlight.current = true;
    setTogglingStatus(true);
    try {
      const updated = await updateLeague(leagueId, { status: next });
      setLeague(updated);
      notifications.success(
        next === "archived" ? "League archived." : "League unarchived.",
      );
    } catch (err) {
      // Failure (incl. RBAC 403): honest notifications, NO phantom badge flip — the
      // status comes only from the server response on success.
      notifications.error(
        err instanceof Error
          ? err.message
          : next === "archived"
            ? "Could not archive the league."
            : "Could not unarchive the league.",
      );
    } finally {
      statusInFlight.current = false;
      setTogglingStatus(false);
    }
  };

  const openCreateSeason = () => {
    setSeasonName("");
    setSeasonStart("");
    setSeasonEnd("");
    setSeasonOpen(true);
  };

  // The create button is gated on the same rules the handler enforces: a
  // trimmed name AND a start AND an end AND end >= start. Drives both the
  // disabled state and an early return in the handler (defence in depth).
  const seasonValid =
    seasonName.trim().length > 0 &&
    seasonStart !== "" &&
    seasonEnd !== "" &&
    seasonEnd >= seasonStart;

  const handleCreateSeason = async () => {
    const trimmedName = seasonName.trim();
    // Client-side gates: required name + both dates + end >= start. None of
    // these ever round-trips an invalid POST.
    if (!trimmedName) return;
    if (!seasonStart || !seasonEnd) return;
    if (seasonEnd < seasonStart) return;
    if (seasonInFlight.current) return;
    seasonInFlight.current = true;
    setSavingSeason(true);
    try {
      // The component↔client boundary is bare date strings; the client widens
      // them to ISO datetimes.
      const created = await createSeason(leagueId, {
        name: trimmedName,
        start_date: seasonStart,
        end_date: seasonEnd,
      });
      setSeasons((prev) => [...prev, created]);
      // A successful create clears any prior seasons-load error banner — the
      // list is now demonstrably loadable.
      setSeasonsError(null);
      notifications.success("Season created.");
      setSeasonOpen(false);
    } catch (err) {
      // Failure (incl. RBAC 403): honest notifications, dialog stays OPEN, no phantom row.
      notifications.error(
        err instanceof Error ? err.message : "Could not create the season.",
      );
    } finally {
      seasonInFlight.current = false;
      setSavingSeason(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 }, pb: 2 }}>
      {loading ? (
        <Stack aria-label="Loading league" role="status" spacing={1}>
          <Skeleton height={36} variant="rounded" width={256} />
          <Skeleton height={24} variant="rounded" width={160} />
        </Stack>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : league ? (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              alignItems: { sm: "flex-start" },
              justifyContent: "space-between",
            }}
          >
            <Stack spacing={1}>
              <Typography component="h1" sx={{ fontWeight: 700 }} variant="h5">
                {league.name}
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center" }}
              >
                <Typography color="text.secondary" variant="body2">
                  {LEAGUE_FORMAT_LABELS[league.format as LeagueFormat] ??
                    league.format}
                </Typography>
                <Chip
                  color={league.status === "active" ? "success" : "default"}
                  label={STATUS_LABELS[league.status] ?? league.status}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button onClick={openEdit} size="small" variant="outlined">
                Edit
              </Button>
              <Button
                disabled={togglingStatus}
                onClick={() => void handleToggleStatus()}
                size="small"
                variant="outlined"
              >
                {league.status === "archived" ? "Unarchive" : "Archive"}
              </Button>
            </Stack>
          </Stack>

          {league.description ? (
            <Typography color="text.secondary" variant="body2">
              {league.description}
            </Typography>
          ) : null}

          <Stack component="section" spacing={2}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography component="h2" variant="h6">
                Seasons
              </Typography>
              {sortedSeasons.length > 0 ? (
                <Button
                  onClick={openCreateSeason}
                  size="small"
                  variant="contained"
                >
                  New season
                </Button>
              ) : null}
            </Stack>

            {seasonsError ? (
              <Alert severity="error">{seasonsError}</Alert>
            ) : sortedSeasons.length === 0 ? (
              <Paper
                sx={{
                  borderStyle: "dashed",
                  px: 2,
                  py: 6,
                  textAlign: "center",
                }}
                variant="outlined"
              >
                <Typography component="h3" variant="subtitle1">
                  No seasons yet
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Create the first season to start scheduling play.
                </Typography>
                <Button
                  onClick={openCreateSeason}
                  size="small"
                  sx={{ mt: 2 }}
                  variant="contained"
                >
                  New season
                </Button>
              </Paper>
            ) : (
              <Paper variant="outlined">
                <Stack
                  component="ul"
                  divider={<Divider flexItem />}
                  sx={{ listStyle: "none", m: 0, p: 0 }}
                >
                  {sortedSeasons.map((season) => (
                    <Stack
                      component="li"
                      direction="row"
                      key={season.season_id}
                      spacing={2}
                      sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Box>
                        <Link
                          component={RouterLink}
                          sx={{ fontWeight: 600 }}
                          to={`/leagues/seasons/${season.season_id}`}
                          underline="hover"
                          variant="body2"
                        >
                          {season.name}
                        </Link>
                        <Typography
                          color="text.secondary"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                          variant="body2"
                        >
                          {formatDateRange(season.start_date, season.end_date)}
                        </Typography>
                      </Box>
                      <Chip
                        color={
                          season.status === "active"
                            ? "success"
                            : season.status === "cancelled"
                              ? "error"
                              : "default"
                        }
                        label={
                          SEASON_STATUS_LABELS[season.status] ?? season.status
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}
          </Stack>
        </>
      ) : null}

      <Dialog
        aria-labelledby="edit-league-title"
        onClose={() => {
          if (!savingEdit) setEditOpen(false);
        }}
        open={editOpen}
      >
        <DialogTitle id="edit-league-title">Edit league</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              disabled={savingEdit}
              id="edit-league-name"
              label="Name"
              onChange={(event) => setEditName(event.target.value)}
              required
              size="small"
              slotProps={{ htmlInput: { maxLength: 255 } }}
              value={editName}
            />

            <FormControl disabled={savingEdit} size="small">
              <InputLabel id="edit-league-format-label">Format</InputLabel>
              <Select
                id="edit-league-format"
                label="Format"
                labelId="edit-league-format-label"
                onChange={(event) =>
                  setEditFormat(event.target.value as LeagueFormat)
                }
                value={editFormat}
              >
                {LEAGUE_FORMATS.map((leagueFormat) => (
                  <MenuItem key={leagueFormat} value={leagueFormat}>
                    {LEAGUE_FORMAT_LABELS[leagueFormat]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              disabled={savingEdit}
              id="edit-league-description"
              label="Description"
              minRows={3}
              multiline
              onChange={(event) => setEditDescription(event.target.value)}
              size="small"
              value={editDescription}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={savingEdit}
            onClick={() => setEditOpen(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            disabled={savingEdit || !editName.trim()}
            onClick={() => void handleEdit()}
            size="small"
            variant="contained"
          >
            {savingEdit ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-labelledby="new-season-title"
        onClose={() => {
          if (!savingSeason) setSeasonOpen(false);
        }}
        open={seasonOpen}
      >
        <DialogTitle id="new-season-title">New season</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              disabled={savingSeason}
              id="season-name"
              label="Name"
              onChange={(event) => setSeasonName(event.target.value)}
              placeholder="e.g. Fall 2026"
              required
              size="small"
              slotProps={{ htmlInput: { maxLength: 255 } }}
              value={seasonName}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                disabled={savingSeason}
                fullWidth
                id="season-start"
                label="Start date"
                onChange={(event) => setSeasonStart(event.target.value)}
                required
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                type="date"
                value={seasonStart}
              />
              <TextField
                disabled={savingSeason}
                fullWidth
                id="season-end"
                label="End date"
                onChange={(event) => setSeasonEnd(event.target.value)}
                required
                size="small"
                slotProps={{
                  htmlInput: { min: seasonStart || undefined },
                  inputLabel: { shrink: true },
                }}
                type="date"
                value={seasonEnd}
              />
            </Stack>

            {seasonStart && seasonEnd && seasonEnd < seasonStart ? (
              <Alert severity="error">
                The end date must be on or after the start date.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={savingSeason}
            onClick={() => setSeasonOpen(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            disabled={savingSeason || !seasonValid}
            onClick={() => void handleCreateSeason()}
            size="small"
            variant="contained"
          >
            {savingSeason ? "Creating…" : "Create season"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
